import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { AppView, AppMainView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import moment from 'moment'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { POSShifts } from './POSShifts'
import { InventoryAudit } from './InventoryAudits'
import { Onboarding } from './Onboarding'
import { AnnounceKitBadge } from '../../../components/AnnounceKitBadge'
import Helmet from 'react-helmet'
import { Icon } from 'antd'
import { capitalizeFirstLetter } from '../../../utilities/helpers'
import { PermissionNames } from '@getgreenline/homi-shared'
import { DashboardImageCards } from './DashboardImageCards/DashboardImageCards'
import { BLAZE_COMPANY } from '../../../constants/general'
import { ExternalLinks, HelpLinks } from '../../../constants/ExternalLinks'
import BlazeEcomLogo from '../../../images/blaze-ecom.svg'
import BlazepayLogo from '../../../images/blaze-icons/blazepay.png'
import SampleDirectLogo from '../../../images/sample-direct.png'
import GrCapitalLogo from '../../../images/greenline-capital.png'
import { Routes } from '../../../constants/Routes'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardHome extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
  }

  async componentDidMount() {
    await this.props.currentCompanyStore!.getEntityUsers()
  }

  updateOnboarding() {
    const company = this.props.currentCompanyStore!.company!
    this.props.currentCompanyStore!.update({ isOnboarding: !company.isOnboarding })
  }

  getGreetingTime() {
    const now = moment()
    const splitAfternoon = 12 // 24hr time to split the afternoon
    const splitEvening = 17 // 24hr time to split the evening
    const currentHour = parseFloat(now.format('HH'))

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      // Between 12 PM and 5PM
      return 'afternoon'
    } else if (currentHour >= splitEvening) {
      // Between 5PM and Midnight
      return 'evening'
    }
    // Between dawn and noon
    return 'morning'
  }

  render() {
    const user = this.props.currentUserStore!.currentUser!
    const company = this.props.currentCompanyStore!.company!
    const firstName = user.name.split(' ')[0]
    const userCanViewReports = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_REPORTS,
      company.id,
    )

    return (
      <AppView column>
        <Helmet>
          <title>Home</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader
          title={`Good ${this.getGreetingTime()}, ${capitalizeFirstLetter(firstName.trim())}`}
        />
        <AppMainView className='background-grey'>
          <div className='row mt-1'>
            <div className={`col-lg-${company.isOnboarding ? '6' : '4'}`}>
              <DashboardImageCards
                cards={[
                  {
                    title: 'BLAZEPAY',
                    isExternalUrl: true,
                    items: [
                      'Centralized Merchant Portal',
                      'Accept Major Credit/Debit Cards',
                      'Faster Transactions In-Store and Online',
                      'Easy-to-Connect Payment Terminals',
                    ],
                    buttonText: 'Get approved',
                    buttonUrl: ExternalLinks.BLAZEPAY_DASHBOARD,
                    imageUrl: BlazepayLogo,
                  },
                  {
                    title: 'Sample Direct',
                    isLoading: false,
                    imageUrl: SampleDirectLogo,
                    items: ['Order samples', 'Check upcoming drops', "See what's trending"],
                    buttonText: 'Check out',
                    buttonUrl: Routes.DASHBOARD_SAMPLE_DIRECT(
                      this.props.currentCompanyStore?.company?.id || 0,
                    ),
                  },
                  {
                    title: 'BLAZE ECOM',
                    isExternalUrl: true,
                    imageUrl: BlazeEcomLogo,
                    items: [
                      'Fast and easy to set up',
                      'Accurate inventory and orders',
                      'Manage everything from one place',
                      'Custom DNS, SEO, delivery support',
                    ],
                    buttonText: 'Sign up',
                    buttonUrl: ExternalLinks.FORM_ONLINE_ORDERING_WAITLIST,
                  },
                  {
                    title: 'Greenline Capital',
                    isExternalUrl: true,
                    imageUrl: GrCapitalLogo,
                    items: [
                      'Loans up to $800k',
                      'Line of credit up to $125k',
                      'Approved within 24hrs',
                    ],
                    buttonText: 'Get approved',
                    buttonUrl: ExternalLinks.GREENLINE_CAPITAL,
                  },
                ]}
              />
              <br />
            </div>
            <div className={`col-lg-${company.isOnboarding ? '6' : '4'}`}>
              {company.isOnboarding ? (
                <div className='card mb-4'>
                  <div className='card-body p-4'>
                    <Onboarding
                      companyId={company.id}
                      completeOnboarding={() => this.updateOnboarding()}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  {userCanViewReports && (
                    <div>
                      <div className='card mb-4' style={{ minHeight: 400 }}>
                        <div className='card-body p-4'>
                          <POSShifts />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className='card mb-4' style={{ minHeight: 350 }}>
                    <div className='card-body p-4'>
                      <InventoryAudit />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`col-lg-${company.isOnboarding ? '6' : '4'}`}>
              <div className='card mb-4' style={{ minHeight: 400 }}>
                <div className='card-body p-4'>
                  <h6>
                    <Icon type='notification' style={{ marginRight: 5 }} />
                    BLAZE Canada Updates
                  </h6>
                  <hr />
                  <div className='text-muted d-inline-flex align-items-center'>
                    <a className='ak-trigger mr-2'>Read update notes</a>
                    <AnnounceKitBadge
                      userId={user.id}
                      email={user.email}
                      companyId={company.id}
                      companyName={company.name}
                      province={company.province}
                    />
                  </div>
                  <br />
                  <br />
                  <br />
                  <h6>
                    <Icon type='book' style={{ marginRight: 5 }} />
                    Support manuals
                  </h6>
                  <hr />
                  <div>
                    <b>Recommended</b>
                  </div>
                  <div>
                    <a href={HelpLinks.SETTINGS} target='_blank' rel='noreferrer'>
                      1. Company setup (locations, receipts, PO costs)
                    </a>
                  </div>
                  <div>
                    <a href={HelpLinks.POS_HARDWARE_SETUP} target='_blank' rel='noreferrer'>
                      2. Hardware (scanners, printers, terminals)
                    </a>
                  </div>
                  <div>
                    <a href={HelpLinks.ADDING_FIRST_INVENTORY} target='_blank' rel='noreferrer'>
                      3. Adding your first inventory
                    </a>
                  </div>
                  <div>
                    <a href={HelpLinks.MAKING_FIRST_SALES} target='_blank' rel='noreferrer'>
                      {' '}
                      4. Making your first sales
                    </a>
                  </div>
                  <div>
                    <a href={HelpLinks.INTEGRATIONS} target='_blank' rel='noreferrer'>
                      5. Integrations (click-and-collect, gift cards)
                    </a>
                  </div>
                  <div>
                    <a href={HelpLinks.HELP_CENTER} target='_blank' rel='noreferrer'>
                      6. All manuals
                    </a>
                  </div>
                  <br />
                  <br />
                  <h6>
                    <Icon type='message' style={{ marginRight: 5 }} />
                    Get in touch
                  </h6>
                  <hr />
                  <div>
                    <b>Live chat:</b> Bottom-right chat icon
                  </div>
                  <div className='text-muted'>Get replies to quick questions.</div>
                  <div>
                    <b>Phone:</b> <a href='tel:866-845-6931'>866-845-6931</a>
                  </div>
                  <div className='text-muted'>
                    Mon-Fri <b>6am-5pm PST</b> Sat-Sun <b>8am-4pm PST</b>
                  </div>
                  <div>
                    <b>Email:</b> <a href='mailto:support-ca@blaze.me'>support-ca@blaze.me</a>
                  </div>
                  <div className='text-muted'>For more complicated requests.</div>
                  <br />
                  <br />
                  <h6>
                    <Icon type='team' style={{ marginRight: 5 }} />
                    Training sessions
                  </h6>
                  <hr />
                  <div className='text-muted mb-2'>
                    Click{' '}
                    <a href='https://forms.gle/g7NDLoKmwcW765Et5' target='_blank' rel='noreferrer'>
                      {' '}
                      here{' '}
                    </a>{' '}
                    to sign up for {BLAZE_COMPANY} University's free online training today!
                  </div>
                  <div className='text-muted mb-2'>
                    <b>Which training program should I sign up for?</b>
                  </div>
                  <div className='text-muted mb-2'>
                    <ul style={{ paddingLeft: 16 }}>
                      <li style={{ paddingBottom: 8 }}>
                        Owners and managers {'>'} Sign up for{' '}
                        <i>“Both the Dashboard & POS training [your province]”</i>
                      </li>
                      <li style={{ paddingBottom: 8 }}>
                        Budtenders and sales staff {'>'} Sign up for{' '}
                        <i>“POS Only- All provinces”</i>
                      </li>
                      <li>
                        Finance and accounting {'>'} Sign up for <i>“Dashboard: Finance Only”</i>
                      </li>
                    </ul>
                    A unique email address is required per person to sign up for training.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={{ height: 200 }} />
        </AppMainView>
      </AppView>
    )
  }
}
